import { useMemo, useState } from 'react'
import { Box, Flex, Table as ChakraTable, Text, useT } from '@minetrace/ui'
import { motion } from 'framer-motion'
import { LuArrowDownWideNarrow, LuChevronRight, LuSearch, LuX } from 'react-icons/lu'
import type { IndiceEntradaResponse } from '../../../api/models/response/indiceDigital'
import { accentFor, fonts, mineral, shadows } from '../../../theme'
import { formatDate } from '../../../lib/dates'
import { formatKb } from './helpers'
import { OrigenBadge } from './badges'

const MotionRow = motion.create(ChakraTable.Row)

/** Expedientes vive en Identidad: rodocrosita. */
const acc = accentFor('identity')

/** `ExpedienteItemType.Caratula` (shared/MineTrace.Shared/Enums/ExpedienteItemType.cs). */
const ITEM_TYPE_CARATULA = 0

/**
 * EL ÍNDICE FOLIADO — la tabla de documentos del expediente.
 *
 * DOS NUMERACIONES QUE NO SON LA MISMA:
 *  • El **correlativo** (1..N) identifica al documento dentro del expediente.
 *  • El **foliado** (página inicial → final) es corrido a lo largo de todo el
 *    expediente: la fila N empieza donde terminó la N-1.
 * Ambas las calcula el backend en el mismo recorrido, así que no pueden divergir.
 * Las cuatro columnas del foliado van agrupadas bajo un mismo fondo para que se
 * lean como un bloque, separadas del correlativo.
 *
 * SE MUESTRA EL MÁS NUEVO ARRIBA, que es como se lee un expediente: lo último
 * incorporado es lo que interesa. EXCEPCIÓN: la carátula (folio 1) va siempre
 * primero, como la tapa de un expediente físico. El foliado NO se recalcula por
 * nada de esto — el documento 1 sigue ocupando el folio 1.
 * Aquí sólo se invierte la presentación y se FILTRA; nunca se reordena el fondo.
 *
 * La serie documental y su retención no se muestran en ninguna parte de esta
 * pantalla: la vigencia es del expediente completo, no de cada papel que se le
 * adjunta. El dato se sigue calculando y viaja en la respuesta; queda pendiente
 * retirarlo del modelo.
 */
export function IndiceTable({
  entradas,
  onSelect,
}: {
  entradas: IndiceEntradaResponse[]
  onSelect: (entrada: IndiceEntradaResponse) => void
}) {
  const { t } = useT()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const matching = q
      ? entradas.filter(
        (e) =>
          e.titulo.toLowerCase().includes(q) ||
          String(e.correlativo).includes(q) ||
          (e.nombreArchivo?.toLowerCase().includes(q) ?? false),
      )
      : entradas
    // El backend entrega en orden de foliado (1..N). Se invierte SOLO para mostrar:
    // slice() evita voltear el array del padre, que sigue siendo el orden real.
    const reversed = matching.slice().reverse()
    // La carátula es la tapa del expediente: va arriba aunque sea el folio 1 y la
    // lista se lea del más nuevo al más antiguo. Arrastra a sus anexos (p. ej. los
    // reportes de Estabilidad de la Labor que cuelgan de ella), el folio no cambia.
    const caratula = entradas.find(e => e.itemType === ITEM_TYPE_CARATULA && !e.parentItemId)
    if (!caratula) return reversed
    const deCaratula = (e: IndiceEntradaResponse) =>
      e.orden === caratula.orden || e.orden.startsWith(`${caratula.orden}.`)
    const tapa = reversed.filter(deCaratula)
    const tapaOrdenada = [...tapa.filter(e => e.orden === caratula.orden), ...tapa.filter(e => e.orden !== caratula.orden)]
    return [...tapaOrdenada, ...reversed.filter(e => !deCaratula(e))]
  }, [entradas, query])

  const folioBg = `${acc.color}0a`

  const headerProps = {
    px: 3,
    py: 3,
    fontSize: '10px',
    fontWeight: '600',
    fontFamily: fonts.mono,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: mineral.ink3,
    borderColor: mineral.line,
    whiteSpace: 'nowrap' as const,
  }
  const folioHeaderProps = { ...headerProps, bg: `${acc.color}12`, color: acc.deep, textAlign: 'right' as const }

  const cellProps = {
    px: 3,
    py: 3,
    borderColor: mineral.line,
    whiteSpace: 'nowrap' as const,
  }
  const folioCellProps = { ...cellProps, bg: folioBg, textAlign: 'right' as const }

  return (
    <Box>
      {/* Barra de filtros */}
      <Flex gap={3} mb={3} flexWrap="wrap" alignItems="center">
        <Flex flex="1" minW="240px" maxW="520px" align="center" gap={2.5} h="42px" px={3.5} borderRadius="12px" bg={mineral.surface}
          border={`1px solid ${query ? acc.color : mineral.lineStrong}`} boxShadow={query ? `0 0 0 3px ${acc.color}1a` : 'none'}
          transition="border-color .2s, box-shadow .2s">
          <Box color={query ? acc.color : mineral.ink3} display="flex" transition="color .2s"><LuSearch size={16} /></Box>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('expedientes.electronico.table.search')}
            aria-label={t('expedientes.electronico.table.search')}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: mineral.ink }}
          />
          {query && (
            <Box as="button" {...{ type: 'button', 'aria-label': 'Limpiar' }} display="flex" p={1} borderRadius="6px" color={mineral.ink3}
              cursor="pointer" _hover={{ bg: mineral.sunk, color: mineral.ink }} onClick={() => setQuery('')}>
              <LuX size={14} />
            </Box>
          )}
        </Flex>

        <Flex align="center" gap={1.5} fontSize="12px" color={mineral.ink3} title={t('expedientes.electronico.table.orderNote')}>
          <LuArrowDownWideNarrow size={14} />
          Más reciente primero · carátula arriba
        </Flex>

        <Text ml="auto" fontFamily={fonts.mono} fontSize="12px" color={mineral.ink3}>
          {t('expedientes.electronico.table.showing', { shown: filtered.length, total: entradas.length })}
        </Text>
      </Flex>

      <Box border={`1px solid ${mineral.line}`} borderRadius="16px" overflow="hidden" bg={mineral.surface} boxShadow={shadows.sm}>
        <Box overflowX="auto">
          <ChakraTable.Root size="sm">
            <ChakraTable.Header>
              <ChakraTable.Row bg={mineral.surfaceAlt}>
                <ChakraTable.ColumnHeader {...headerProps} w="56px" textAlign="right">
                  {t('expedientes.electronico.table.correlativo')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps} minW="260px">
                  {t('expedientes.electronico.table.nombre')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps}>
                  {t('expedientes.electronico.table.fecha')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps}>
                  {t('expedientes.electronico.table.fechaExpediente')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps}>
                  {t('expedientes.electronico.table.tipo')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps} minW="180px">
                  {t('expedientes.electronico.table.archivo')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps} textAlign="right">
                  {t('expedientes.electronico.table.peso')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...folioHeaderProps}>
                  {t('expedientes.electronico.table.pagIni')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...folioHeaderProps}>
                  {t('expedientes.electronico.table.pagFin')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...folioHeaderProps}>
                  {t('expedientes.electronico.table.nroPag')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...folioHeaderProps}>
                  {t('expedientes.electronico.table.folios')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps}>
                  {t('expedientes.electronico.table.extension')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps}>
                  {t('expedientes.electronico.table.hash')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader {...headerProps} w="44px" />
              </ChakraTable.Row>
            </ChakraTable.Header>

            <ChakraTable.Body>
              {filtered.map((e, idx) => {
                const isCaratula = e.itemType === ITEM_TYPE_CARATULA && !e.parentItemId
                return (
                  <MotionRow
                    key={e.itemId}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, delay: Math.min(idx, 15) * 0.012 }}
                    cursor="pointer"
                    className="k-indice-row"
                    _hover={{ bg: `${acc.color}08` }}
                    onClick={() => onSelect(e)}
                  >
                    <ChakraTable.Cell {...cellProps} textAlign="right">
                      <Text fontFamily={fonts.mono} fontSize="12px" fontWeight={700} color={mineral.ink}>
                        {e.correlativo}
                      </Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps} whiteSpace="normal">
                      <Flex align="center" gap={2}>
                        {isCaratula && (
                          <Box px={1.5} py="1px" borderRadius="5px" bg={acc.color} color="#fff" fontFamily={fonts.mono} fontSize="9.5px"
                            fontWeight={700} letterSpacing="0.08em" flexShrink={0}>CARÁTULA</Box>
                        )}
                        <Text fontSize="13.5px" fontWeight={600} color={mineral.ink}>{e.titulo}</Text>
                      </Flex>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <Text fontSize="12.5px" color={mineral.ink2}>{formatDate(e.fechaProduccion)}</Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <Text fontSize="12.5px" color={mineral.ink2}>
                        {e.fechaIncorporacion ? formatDate(e.fechaIncorporacion) : '—'}
                      </Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <OrigenBadge modo={e.modo} />
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <Text fontSize="11.5px" fontFamily={fonts.mono} color={mineral.ink3} truncate maxW="220px" title={e.nombreArchivo ?? undefined}>
                        {e.nombreArchivo ?? '—'}
                      </Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps} textAlign="right">
                      <Text fontSize="12px" fontFamily={fonts.mono} color={mineral.ink2}>{formatKb(e.tamanoBytes)}</Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...folioCellProps}>
                      <Text fontSize="12.5px" fontFamily={fonts.mono} fontWeight={700} color={mineral.ink}>{e.folioDesde}</Text>
                    </ChakraTable.Cell>
                    <ChakraTable.Cell {...folioCellProps}>
                      <Text fontSize="12.5px" fontFamily={fonts.mono} fontWeight={700} color={mineral.ink}>{e.folioHasta}</Text>
                    </ChakraTable.Cell>
                    <ChakraTable.Cell {...folioCellProps}>
                      <Text fontSize="12px" fontFamily={fonts.mono} color={mineral.ink2}>{e.paginas}</Text>
                    </ChakraTable.Cell>
                    <ChakraTable.Cell {...folioCellProps}>
                      <Text fontSize="12px" fontFamily={fonts.mono} color={mineral.ink2}>{e.folios}</Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <Text fontSize="11px" fontFamily={fonts.mono} textTransform="uppercase" color={mineral.ink3}>{e.extension || '—'}</Text>
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      {e.resumenHash ? (
                        <Box as="span" px={1.5} py="2px" borderRadius="5px" bg={mineral.sunk} fontFamily={fonts.mono} fontSize="10.5px" color={mineral.ink2}
                          title={e.resumenHash}>
                          {e.resumenHash.slice(0, 10)}…
                        </Box>
                      ) : <Text fontSize="11px" color={mineral.ink4}>—</Text>}
                    </ChakraTable.Cell>

                    <ChakraTable.Cell {...cellProps}>
                      <Box color={mineral.ink4} aria-label={t('expedientes.electronico.table.detail')} display="flex"
                        transition="transform .15s, color .15s" css={{ '.k-indice-row:hover &': { transform: 'translateX(3px)', color: acc.color } }}>
                        <LuChevronRight size={15} />
                      </Box>
                    </ChakraTable.Cell>
                  </MotionRow>
                )
              })}
            </ChakraTable.Body>
          </ChakraTable.Root>
        </Box>

        {filtered.length === 0 && (
          <Text fontSize="sm" color={mineral.ink3} textAlign="center" py={10}>
            {entradas.length === 0
              ? t('expedientes.electronico.table.empty')
              : t('expedientes.electronico.table.emptyFiltered')}
          </Text>
        )}
      </Box>
    </Box>
  )
}
